type QueueEntry = {
  key: string | null;
  resolve: () => void;
  reject: (reason: unknown) => void;
};

export type LimiterSlot = {
  /**
   * Resolves once a slot has been granted. May reject with `ConcurrencyLimiterSuperseded`
   * if a newer acquire with the same key evicts this one while it is still queued.
   */
  granted: Promise<void>;
  /**
   * Release the slot. Safe to call exactly once regardless of whether the slot was ever
   * granted:
   * - If the slot was granted, it frees the active slot and hands it to the next waiter.
   * - If the slot is still queued (e.g. the caller was cancelled before being granted),
   *   it removes the queued entry so it can never later be handed a phantom slot.
   * Calling release more than once is a no-op after the first call.
   */
  release: () => void;
};

export function createConcurrencyLimiter(maxConcurrent: number) {
  let active = 0;
  const queue: QueueEntry[] = [];

  function grantNext(): void {
    const next = queue.shift();
    if (next) {
      active++;
      next.resolve();
    }
  }

  /**
   * Acquire a slot in the limiter. If all slots are busy, the caller waits in a queue.
   *
   * @param key - Optional deduplication key (e.g. visualizationId). When provided, any
   *   previously queued (not yet started) entry with the same key is evicted. This prevents
   *   stale queries from occupying a slot when a newer query for the same resource arrives.
   *   The evicted entry's promise rejects with a `ConcurrencyLimiterSuperseded` error.
   *
   * Returns a {@link LimiterSlot}. The caller MUST call `slot.release()` exactly once in a
   * `finally` block — whether the acquire succeeded, failed, or was abandoned/cancelled while
   * still queued. `release()` is cancellation-safe: releasing a slot that is still queued
   * removes it from the queue instead of decrementing the active count, so a cancelled waiter
   * can never leak a slot.
   */
  function acquire(key?: string): LimiterSlot {
    // Deduplicate: remove any queued entry with the same key (not yet running)
    if (key) {
      const idx = queue.findIndex((entry) => entry.key === key);
      if (idx !== -1) {
        const [evicted] = queue.splice(idx, 1);
        evicted.reject(new ConcurrencyLimiterSuperseded());
      }
    }

    // State for this specific acquisition, closed over by release().
    let granted = false;
    let released = false;
    let queueEntry: QueueEntry | null = null;

    let grantedPromise: Promise<void>;
    if (active < maxConcurrent) {
      active++;
      granted = true;
      grantedPromise = Promise.resolve();
    } else {
      grantedPromise = new Promise<void>((resolve, reject) => {
        queueEntry = {
          key: key ?? null,
          resolve: () => {
            granted = true;
            queueEntry = null;
            resolve();
          },
          reject: (reason) => {
            queueEntry = null;
            reject(reason);
          },
        };
        queue.push(queueEntry);
      });
    }

    function release(): void {
      if (released) return;
      released = true;

      if (granted) {
        // We held an active slot — free it and promote the next waiter.
        active--;
        grantNext();
        return;
      }

      // Not granted yet: either we are still sitting in the queue, or we were evicted
      // by a superseding acquire with the same key.
      //
      // - Still queued: `queueEntry` is set, so we remove it here. This is what prevents a
      //   future release() from shifting a dead entry and leaking a slot. No active count
      //   change because we never incremented it.
      // - Evicted (superseded): the reject callback already set `queueEntry = null` and
      //   removed the entry from the queue, and the caller's `granted` promise rejected with
      //   ConcurrencyLimiterSuperseded (see visualization.ts). release() still runs from the
      //   caller's finally, but with granted=false and queueEntry=null it is a no-op — correct,
      //   since there is nothing left to clean up.
      if (queueEntry) {
        const idx = queue.indexOf(queueEntry);
        if (idx !== -1) queue.splice(idx, 1);
        queueEntry = null;
      }
    }

    return { granted: grantedPromise, release };
  }

  /** Returns current limiter state for diagnostics. */
  function getStats(): { active: number; queued: number; queuedKeys: (string | null)[] } {
    return {
      active,
      queued: queue.length,
      queuedKeys: queue.map((e) => e.key),
    };
  }

  return { acquire, getStats };
}

/**
 * Thrown when a queued acquire is evicted because a newer acquire with the same key arrived.
 * Callers should catch this to silently abort without reporting an error to the user.
 */
export class ConcurrencyLimiterSuperseded extends Error {
  constructor() {
    super('Queued request superseded by a newer request for the same key');
    this.name = 'ConcurrencyLimiterSuperseded';
  }
}
